import React, { Children, forwardRef, useEffect, useImperativeHandle, useState } from 'react'

import cn from 'classnames'

import { BaseDropDownItemProps } from './components/BaseDropDownItem/types'
import styles from './DropDown.module.scss'
import useOnClickOutside from './helpers/useClickOutside'
import { DropDownProps } from './types'

const DropDown: React.FC<DropDownProps> = forwardRef(
  (
    {
      isContentVisible = false,
      placement = 'bottom-start',
      triggerElement,
      role = 'listbox',
      ariaLabel,
      children,
      onSelected,
      onClose,
      onOpen,
      className,
      classNameWrapper,
      isSelect = false,
      id,
      testId,
      isControlled = false,
      isControlledOpened = false,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(isContentVisible)
    const [pointer, setPointer] = useState(0)
    const dropDownRef = React.createRef<HTMLDivElement>()
    const childrenCount = Children.count(children)
    const dropDownId = `dropDown-${ariaLabel}`
    const triggerId = `trigger-${ariaLabel}`

    useImperativeHandle<HTMLDivElement | null, HTMLDivElement | null>(
      ref,
      () => dropDownRef.current,
    )

    const focusTrigger = (): void => {
      document.getElementById(id || triggerId)!.focus()
    }

    const handleOptionSelect = (event, childType): void => {
      event.preventDefault()
      const selectedChild = children[pointer].props
      onSelected({ label: selectedChild.label, metaText: selectedChild.metaText, index: pointer })
      if (childType.name !== 'CheckboxDropDownItem') {
        focusTrigger()
        setIsOpen(false)
      }
    }

    const handlePointer = (index): void => {
      setPointer(index)
      focusTrigger()
    }

    const handleClickOutside = (): void => {
      focusTrigger()
      setIsOpen(false)
    }

    useOnClickOutside(dropDownRef, handleClickOutside)

    useEffect(() => {
      const disableArrowScroll = (event: KeyboardEvent): void => {
        if (isOpen && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
          event.preventDefault()
        }
      }
      document.addEventListener('keydown', disableArrowScroll)

      if (isOpen) {
        if (typeof onOpen === 'function') onOpen()
      } else if (typeof onClose === 'function') onClose()

      return (): void => document.removeEventListener('keydown', disableArrowScroll)
    }, [isOpen])

    useEffect(() => {
      setIsOpen(isContentVisible)
    }, [isContentVisible])

    useEffect(() => {
      if (childrenCount < pointer) setPointer(0)
    }, [childrenCount])

    const onKeyDown = (
      event:
        | KeyboardEvent
        | React.KeyboardEvent<HTMLDivElement>
        | React.KeyboardEvent<HTMLLIElement>,
    ): void => {
      if (!isSelect && event.key !== 'Tab') event.preventDefault()
      if ((event.key === 'ArrowUp' || event.key === 'ArrowDown') && event.altKey) {
        if (!isControlled) {
          setIsOpen(!isOpen)
        }
        return
      }
      if (event.key === 'Tab') {
        setIsOpen(false)
        return
      }
      if (event.key === 'Escape') {
        setIsOpen(false)
        focusTrigger()
        return
      }
      if (event.key === 'Enter' || (!isSelect && event.key === ' ')) {
        if (isOpen && childrenCount > 0) {
          const selectedChild = children[pointer].props
          onSelected({
            label: selectedChild.label,
            metaText: selectedChild.metaText,
            index: pointer,
          })

          if (children[pointer].type.name !== 'CheckboxDropDownItem') {
            setIsOpen(false)
            focusTrigger()
          }
        } else {
          setIsOpen(true)
        }
        return
      }
      if (event.key === 'ArrowDown') {
        if (!isOpen) {
          setIsOpen(true)
        } else if (childrenCount - 1 > pointer) {
          setPointer(pointer + 1)
        }
        return
      }
      if (event.key === 'ArrowUp') {
        if (!isOpen) {
          setIsOpen(true)
        } else if (pointer > 0) {
          setPointer(pointer - 1)
        }
        return
      }
      if (event.key === 'Home') {
        if (!isOpen) setIsOpen(true)
        setPointer(0)
        return
      }
      if (event.key === 'End') {
        if (!isOpen) setIsOpen(true)
        setPointer(childrenCount - 1)
        return
      }
      if (event.key === 'PageUp') {
        if (pointer - 10 < 0) {
          setPointer(0)
        } else {
          setPointer(pointer - 10)
        }
        return
      }
      if (event.key === 'PageDown') {
        if (pointer + 10 > childrenCount) {
          setPointer(childrenCount - 1)
        } else {
          setPointer(pointer + 10)
        }
      }
    }

    return (
      <div className={cn(styles.dropDown, classNameWrapper)} ref={dropDownRef} data-testid={testId}>
        {React.cloneElement(triggerElement, {
          onClick: () => {
            if (!isControlled) {
              setIsOpen(!isOpen)
            }
          },
          onKeyDown,
          'aria-controls': dropDownId,
          'aria-expanded': isOpen,
          'aria-haspopup': true,
          'aria-label': ariaLabel,
          id: id || triggerId,
          tabIndex: 0,
          role: 'combobox',
        })}
        {((!isControlled && isOpen) || (isControlled && isControlledOpened)) && (
          <ul
            role={role}
            id={dropDownId}
            tabIndex={-1}
            className={cn(styles.content, styles[placement], className)}
          >
            {React.Children.map(children, (child, index) =>
              React.cloneElement(child as React.ReactElement<BaseDropDownItemProps>, {
                onClick: (event) => handleOptionSelect(event, child.type),
                onKeyDown,
                onMouseOver: () => handlePointer(index),
                isActive: index === pointer,
              }),
            )}
          </ul>
        )}
      </div>
    )
  },
)

export default DropDown
